import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Alert, Modal, Form, Table, Badge } from 'react-bootstrap';
import { FaEdit, FaTrash, FaFileDownload, FaArrowLeft, FaUser, FaCalendarAlt, FaCreditCard, FaCheck } from 'react-icons/fa';
import api from '../../services/api';
import { toast } from 'react-toastify';
import Spinner from '../../components/layout/Spinner';
import AuthContext from '../../context/AuthContext';

const InvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useContext(AuthContext);
  
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState({
    paymentMethod: '',
    paymentDate: new Date().toISOString().split('T')[0],
    amountPaid: 0
  });
  const [paymentErrors, setPaymentErrors] = useState({});
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
            const res = await api.get(`/invoices/${id}`);
      setInvoice(res.data.data);
      
      // Initialize payment amount with remaining balance
      if (res.data.data) {
        const remainingBalance = res.data.data.total - res.data.data.amountPaid;
        setPaymentData({
          ...paymentData,
          amountPaid: remainingBalance
        });
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching invoice data:', err);
      setError('Failed to load invoice data');
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
            await api.delete(`/invoices/${id}`);
      toast.success('Invoice deleted successfully');
      navigate('/invoices');
    } catch (err) {
      console.error('Error deleting invoice:', err);
      toast.error(err.response?.data?.msg || 'Failed to delete invoice');
    }
    setShowDeleteModal(false);
  };

  const handleDownload = async () => {
    try {
            const response = await api.get(`/invoices/${id}/pdf`, {
        responseType: 'blob'
      });
      
      // Create a blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoice.invoiceNumber}.pdf`);
      
      // Append to html page
      document.body.appendChild(link);
      
      // Force download
      link.click();
      
      // Clean up and remove the link
      link.parentNode.removeChild(link);
      
      toast.success('Invoice downloaded successfully');
    } catch (err) {
      console.error('Error downloading invoice:', err);
      toast.error('Failed to download invoice');
    }
  };

  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    setPaymentData({
      ...paymentData,
      [name]: name === 'amountPaid' ? parseFloat(value) || 0 : value
    });
    
    // Clear field error when user types
    if (paymentErrors[name]) {
      setPaymentErrors({
        ...paymentErrors,
        [name]: ''
      });
    }
  };

  const validatePaymentForm = () => {
    const errors = {};
    
    if (!paymentData.paymentMethod) {
      errors.paymentMethod = 'Payment method is required';
    }
    
    if (!paymentData.paymentDate) {
      errors.paymentDate = 'Payment date is required';
    }
    
    if (paymentData.amountPaid <= 0) {
      errors.amountPaid = 'Amount paid must be greater than 0';
    }
    
    const remainingBalance = invoice.total - invoice.amountPaid;
    if (paymentData.amountPaid > remainingBalance) {
      errors.amountPaid = `Amount cannot exceed the remaining balance of $${remainingBalance.toFixed(2)}`;
    }
    
    setPaymentErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePaymentForm()) {
      return;
    }
    
    setIsSubmittingPayment(true);
    
    try {
            await api.post(`/invoices/${id}/payment`, paymentData);
      toast.success('Payment recorded successfully');
      setShowPaymentModal(false);
      fetchInvoice(); // Refresh invoice data
    } catch (err) {
      console.error('Error recording payment:', err);
      toast.error(err.response?.data?.msg || 'Failed to record payment');
    } finally {
      setIsSubmittingPayment(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge bg="success">Paid</Badge>;
      case 'partial':
        return <Badge bg="warning">Partial</Badge>;
      case 'pending':
        return <Badge bg="danger">Pending</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <Container>
        <Alert variant="danger">{error}</Alert>
        <Button as={Link} to="/invoices" variant="primary">
          <FaArrowLeft className="me-2" /> Back to Invoices
        </Button>
      </Container>
    );
  }

  if (!invoice) {
    return (
      <Container>
        <Alert variant="warning">Invoice not found</Alert>
        <Button as={Link} to="/invoices" variant="primary">
          <FaArrowLeft className="me-2" /> Back to Invoices
        </Button>
      </Container>
    );
  }

  const remainingBalance = invoice.total - invoice.amountPaid;

  return (
    <Container>
      <Row className="mb-4 align-items-center">
        <Col>
          <h1>Invoice #{invoice.invoiceNumber}</h1>
          <p className="text-muted">
            <FaCalendarAlt className="me-1" /> 
            {new Date(invoice.invoiceDate).toLocaleDateString()}
            {invoice.dueDate && (
              <span> (Due: {new Date(invoice.dueDate).toLocaleDateString()})</span>
            )}
          </p>
        </Col>
        <Col xs="auto">
          <Button as={Link} to="/invoices" variant="outline-secondary" className="me-2">
            <FaArrowLeft className="me-1" /> Back
          </Button>
          <Button variant="outline-primary" onClick={handleDownload} className="me-2">
            <FaFileDownload className="me-1" /> Download
          </Button>
          <Button as={Link} to={`/invoices/edit/${id}`} variant="outline-primary" className="me-2">
            <FaEdit className="me-1" /> Edit
          </Button>
          <Button variant="outline-danger" onClick={() => setShowDeleteModal(true)}>
            <FaTrash className="me-1" /> Delete
          </Button>
        </Col>
      </Row>
      
      <Row className="mb-4">
        <Col md={6}>
          <Card className="mb-4">
            <Card.Header>Customer Information</Card.Header>
            <Card.Body>
              <p>
                <strong>Customer:</strong>{' '}
                <Link to={`/customers/${invoice.customer._id}`}>
                  <FaUser className="me-1" /> {invoice.customer.name}
                </Link>
              </p>
              <p><strong>Email:</strong> {invoice.customer.email}</p>
              <p><strong>Phone:</strong> {invoice.customer.phone}</p>
              {invoice.customer.address && (
                <div>
                  <strong>Address:</strong>
                  <p className="mb-0">{invoice.customer.address.street}</p>
                  <p className="mb-0">
                    {invoice.customer.address.city}, {invoice.customer.address.state} {invoice.customer.address.zipCode}
                  </p>
                  <p>{invoice.customer.address.country}</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card className="mb-4">
            <Card.Header>Invoice Status</Card.Header>
            <Card.Body>
              <div className="d-flex justify-content-between mb-3">
                <span>Status:</span>
                <span>{getStatusBadge(invoice.paymentStatus)}</span>
              </div>
              
              <div className="d-flex justify-content-between mb-3">
                <span>Total Amount:</span>
                <span>${invoice.total.toFixed(2)}</span>
              </div>
              
              <div className="d-flex justify-content-between mb-3">
                <span>Amount Paid:</span>
                <span>${invoice.amountPaid.toFixed(2)}</span>
              </div>
              
              <div className="d-flex justify-content-between mb-3">
                <span>Remaining Balance:</span>
                <span>${remainingBalance.toFixed(2)}</span>
              </div>
              
              {invoice.paymentStatus !== 'paid' && (
                <div className="text-center mt-3">
                  <Button 
                    variant="success" 
                    onClick={() => setShowPaymentModal(true)}
                  >
                    <FaCreditCard className="me-1" /> Record Payment
                  </Button>
                </div>
              )}
              
              {invoice.paymentMethod && (
                <div className="mt-4">
                  <p><strong>Payment Method:</strong> {invoice.paymentMethod ? (invoice.paymentMethod.replace('_', ' ').charAt(0).toUpperCase() + invoice.paymentMethod.replace('_', ' ').slice(1)) : 'N/A'}</p>
                  {invoice.paymentDate && (
                    <p><strong>Payment Date:</strong> {new Date(invoice.paymentDate).toLocaleDateString()}</p>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {invoice.prescription && (
        <Row className="mb-4">
          <Col md={12}>
            <Card>
              <Card.Header>Related Prescription</Card.Header>
              <Card.Body>
                <p>
                  <strong>Prescription Date:</strong>{' '}
                  {new Date(invoice.prescription.prescriptionDate).toLocaleDateString()}
                </p>
                <p>
                  <strong>Doctor:</strong> {invoice.prescription.doctorName}
                </p>
                <p>
                  <strong>Type:</strong> {invoice.prescription && invoice.prescription.prescriptionType ? (invoice.prescription.prescriptionType.replace('_', ' ').charAt(0).toUpperCase() + invoice.prescription.prescriptionType.replace('_', ' ').slice(1)) : 'N/A'}
                </p>
                <div className="mt-2">
                  <Button 
                    as={Link} 
                    to={`/prescriptions/${invoice.prescription._id}`} 
                    variant="outline-primary"
                    size="sm"
                  >
                    View Prescription Details
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
      
      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Header>Invoice Items</Card.Header>
            <Card.Body>
              <div className="table-responsive">
                <Table>
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th className="text-center">Quantity</th>
                      <th className="text-end">Unit Price</th>
                      <th className="text-end">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.items.map((item, index) => (
                      <tr key={index}>
                        <td>{item.description}</td>
                        <td className="text-center">{item.quantity}</td>
                        <td className="text-end">${item.unitPrice.toFixed(2)}</td>
                        <td className="text-end">${(item.quantity * item.unitPrice).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="3" className="text-end"><strong>Subtotal</strong></td>
                      <td className="text-end">
                        ${invoice.items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0).toFixed(2)}
                      </td>
                    </tr>
                    {invoice.taxRate > 0 && (
                      <tr>
                        <td colSpan="3" className="text-end">Tax ({invoice.taxRate}%)</td>
                        <td className="text-end">
                          ${(invoice.items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0) * (invoice.taxRate / 100)).toFixed(2)}
                        </td>
                      </tr>
                    )}
                    {invoice.discountRate > 0 && (
                      <tr>
                        <td colSpan="3" className="text-end">
                          Discount {invoice.discountRate > 0 ? `(${invoice.discountRate}%)` : ''}
                        </td>
                        <td className="text-end">
                          -${invoice.discountAmount.toFixed(2)}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td colSpan="3" className="text-end"><strong>Total</strong></td>
                      <td className="text-end"><strong>${invoice.total.toFixed(2)}</strong></td>
                    </tr>
                  </tfoot>
                </Table>
              </div>
              
              {invoice.notes && (
                <div className="mt-4">
                  <h5>Notes</h5>
                  <p>{invoice.notes}</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete Invoice #{invoice.invoiceNumber}?</p>
          <p className="text-danger">
            This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Payment Modal */}
      <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Record Payment</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handlePaymentSubmit}>
          <Modal.Body>
            <p>
              <strong>Invoice:</strong> #{invoice.invoiceNumber}<br />
              <strong>Total Amount:</strong> ${invoice.total.toFixed(2)}<br />
              <strong>Amount Paid:</strong> ${invoice.amountPaid.toFixed(2)}<br />
              <strong>Remaining Balance:</strong> ${remainingBalance.toFixed(2)}
            </p>
            
            <Form.Group className="mb-3" controlId="paymentMethod">
              <Form.Label>Payment Method <span className="text-danger">*</span></Form.Label>
              <Form.Select
                name="paymentMethod"
                value={paymentData.paymentMethod}
                onChange={handlePaymentChange}
                isInvalid={!!paymentErrors.paymentMethod}
              >
                <option value="">Select Payment Method</option>
                <option value="cash">Cash</option>
                <option value="credit_card">Credit Card</option>
                <option value="debit_card">Debit Card</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="check">Check</option>
                <option value="other">Other</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {paymentErrors.paymentMethod}
              </Form.Control.Feedback>
            </Form.Group>
            
            <Form.Group className="mb-3" controlId="paymentDate">
              <Form.Label>Payment Date <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="date"
                name="paymentDate"
                value={paymentData.paymentDate}
                onChange={handlePaymentChange}
                isInvalid={!!paymentErrors.paymentDate}
              />
              <Form.Control.Feedback type="invalid">
                {paymentErrors.paymentDate}
              </Form.Control.Feedback>
            </Form.Group>
            
            <Form.Group className="mb-3" controlId="amountPaid">
              <Form.Label>Amount Paid ($) <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="number"
                name="amountPaid"
                value={paymentData.amountPaid}
                onChange={handlePaymentChange}
                min="0.01"
                step="0.01"
                max={remainingBalance}
                isInvalid={!!paymentErrors.amountPaid}
              />
              <Form.Control.Feedback type="invalid">
                {paymentErrors.amountPaid}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                {remainingBalance > 0 ? (
                  <>
                    <Button 
                      variant="link" 
                      className="p-0" 
                      onClick={() => setPaymentData({...paymentData, amountPaid: remainingBalance})}
                    >
                      Pay full amount
                    </Button>
                  </>
                ) : (
                  'Invoice is fully paid'
                )}
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPaymentModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="success" 
              type="submit"
              disabled={isSubmittingPayment}
            >
              <FaCheck className="me-1" /> {isSubmittingPayment ? 'Processing...' : 'Record Payment'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default InvoiceDetail;
